import React, { useCallback, useMemo } from 'react'
import uniq from 'lodash/uniq'
import { OpenInIcon, InformationIcon, Text } from '@instacart/ids-core'
import { useBooleanState, LoadingDots } from '@retailer-platform/shared-components'
import { useCreateHref } from 'utils/routing/navigation.hooks'
import { useDomainMessages, useDomainMessage } from 'domains/account-management/utils/domain/intl'
import { useRetailerLocationStoreGroupSelectOptions } from 'domains/account-management/components/retailer-location-store-group-select/utils/AccountManagementRetailerLocationSelectStoreGroup.hooks'
import { SelectModal } from './components/SelectModal'
import { ValueDisplay } from './components/ValueDisplay'
import {
  type OnRetailerLocationStoreGroupChange,
  OptionType,
  type RetailerLocationStoreGroup,
  type StoreGroupOption,
} from './utils/types'
import {
  Row,
  SelectContainer,
  StoreGroupLink,
  SelectedCountContainer,
  StyledTextButton,
  StyledButton,
  StoreGroupDisclaimer,
} from './AccountManagementRetailerLocationStoreGroupSelect.styles'

interface AccountManagementRetailerLocationStoreGroupSelectProps {
  onChange: OnRetailerLocationStoreGroupChange
  value: RetailerLocationStoreGroup
  retailerIds: string[]
  isDisabled: boolean
}

export const AccountManagementRetailerLocationStoreGroupSelect: React.FunctionComponent<
  AccountManagementRetailerLocationStoreGroupSelectProps
> = ({ retailerIds, value: inputValue, onChange, isDisabled }) => {
  const [isOpen, openModal, closeModal] = useBooleanState(false)
  const { options, loading, error } = useRetailerLocationStoreGroupSelectOptions(retailerIds)

  const messages = useDomainMessages({
    addStoreButton: 'accountManagementDomain.form.addStore.addStoreButton',
    clearAll: 'accountManagementDomain.form.addStore.clearAll',
    storeGroupsLink: 'accountManagementDomain.form.addStore.storeGroupsLink',
    placeholderMessage: 'accountManagementDomain.internalTerms.retailers.placeholder',
    loadingMessage: 'accountManagementDomain.internalTerms.retailers.loading',
    storeGroupDisclaimer: 'accountManagementDomain.form.addStore.storeGroupDisclaimer',
  })

  const [selectedStoreCount, hasStoreGroupSelected] = useMemo(() => {
    let storeGroupSelected = false
    const allIds: string[] = []
    // using forEach here because reduce really does not like the typing of the group options
    inputValue?.forEach(value => {
      if (value.type === OptionType.StoreGroup) {
        allIds.push(
          ...((value as StoreGroupOption)?.retailerLocations.map(location => location.id) || [])
        )
        storeGroupSelected = true
      } else {
        allIds.push(value.value)
      }
    })
    return [uniq(allIds).length, storeGroupSelected]
  }, [inputValue])

  const selectedStoreCountMessage = useDomainMessage(
    'accountManagementDomain.form.addStore.storesSelected',
    { numStores: selectedStoreCount }
  )

  const storeGroupsUrl = useCreateHref('store-groups')

  const handleRemoveClicked = useCallback(
    (type, value) => {
      if (value === 'allStores') {
        onChange([])
      } else {
        onChange(
          inputValue.filter(
            option => (option.type === type && option.value !== value) || option.type !== type
          )
        )
      }
    },
    [inputValue, onChange]
  )

  const handleClearAll = useCallback(() => {
    onChange([])
  }, [onChange])

  const addButtonDisabled = loading || isDisabled

  return (
    <SelectContainer>
      <Row>
        <StyledButton
          variant="primary"
          onClick={openModal}
          disabled={addButtonDisabled}
          type="button"
          aria-label={messages.addStoreButton}
        >
          {loading ? (
            <LoadingDots testId="retailer-location-store-group-loading" centered height={12} />
          ) : (
            messages.addStoreButton
          )}
        </StyledButton>
        <StoreGroupLink href={storeGroupsUrl}>
          {messages.storeGroupsLink}
          <OpenInIcon />
        </StoreGroupLink>
      </Row>
      <SelectModal
        loading={loading}
        options={options}
        error={error}
        onChange={onChange}
        value={inputValue}
        open={isOpen}
        onClose={closeModal}
      />
      {selectedStoreCount > 0 && (
        <SelectedCountContainer>
          <Text typography="bodyMedium1" color="systemGrayscale70">
            {selectedStoreCountMessage}
          </Text>
          <StyledTextButton onClick={handleClearAll} disabled={isDisabled}>
            {messages.clearAll}
          </StyledTextButton>
        </SelectedCountContainer>
      )}
      <ValueDisplay options={options} value={inputValue} onRemove={handleRemoveClicked} />
      {hasStoreGroupSelected && (
        <StoreGroupDisclaimer>
          <InformationIcon size={15} color="systemGrayscale30" />
          <Text typography="bodySmall2" color="systemGrayscale50">
            {messages.storeGroupDisclaimer}
          </Text>
        </StoreGroupDisclaimer>
      )}
    </SelectContainer>
  )
}
